/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  FilterRestService,
  isJobElementTotal,
  JobElementTotal,
  JobElementType,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  BuilderPermissionSet,
  CMDatesFilter,
  JobStatus,
  OfficeStatus,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  DateTimeTypes,
  EnumDictionary,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  NumberTypes,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingContext,
  WaitingMode,
  WaitingService,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumnLeft,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { CmListBusinessService } from '../../resources/cm-list-business.service';
import { JobListFilter, JobListItem } from '../../resources/cm.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslationService } from '@gh/core-mls';
import {
  conditionalRowPostCreateDecorator,
  GridSettingsService,
  TABLE_HAS_PRESETS,
  textMessageDialogRoute,
  textOverlayRowPostCreate,
} from '@gh/common-internal-shared';
import { createLink, toString } from '@gh/core-util';
import { omit, padStart } from 'lodash';
import { WorkSheetFilter } from '../../resources/cm-job-rest.service';
import { of$ } from '@gh/rx';
import { Office } from '../../../builder-shared/switch-to/resources/office.model';
import { MasterZone } from '../../../builder-shared/switch-to/resources/master-zone.model';

type predefinedFilter = {
  id: string;
  name: string;
};

@Component({
  selector: 'gh-cm-list',
  styles: [require('./cm-list.component.scss')],
  template: require('./cm-list.component.html'),
})
export class CmListComponent implements OnInit, OnDestroy {
  @ViewChild('predefinedFilter') predefinedFilterRef: ElementRef<HTMLInputElement>;
  dataSource: SlicedDataSource<JobListItem>;
  form: FilterableFormState<JobListFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  masterZoneList = [];
  filter: any;
  datesList: EnumDictionary<CMDatesFilter>;
  predefinedFilters: predefinedFilter[];
  officeList = [];
  total$: Observable<any>;
  _total  = new BehaviorSubject<JobElementTotal[]>([{type: JobElementType.Total, id: 'total-count'}]);

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: CmListBusinessService,
              private notifications: NotificationService,
              private router: AppRouter,
              private angRouter: Router,
              private translations: TranslationService,
              private dialogs: DialogService,
              private waitingService: WaitingService,
              private navigationService: BuilderNavigationService,
              private gridService: GridSettingsService,
              private filterService: FilterRestService,
              private waitingContext: WaitingContext,
              private notificationService: NotificationService,
              public gridDefinitionFactory: GridDefinitionFactory) {
    this.total$ = this._total.asObservable();
    this.gridService.setCurrentTable(TABLE_HAS_PRESETS.cmList);
  }

  get isShowInactiveDisabled(): boolean {
    return !!this.form && !!this.form.getControl('officeId').value;
  }

  get canCreateQuickJob(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.Jobs.CREATE_QUICK_JOB);
  }

  get isResetVisible(): boolean {
    return this.form.getControl('dateType').value
      || this.form.getControl('dateFrom').value
      || this.form.getControl('dateTo').value;
  }

  isOfficeDeactivated(office: any): boolean {
    return !!office.status && office.status === OfficeStatus.Deactivated;
  }

  isOfficeBlocked(office: any): boolean {
    return !!office.status && office.status === OfficeStatus.Blocked;
  }

  isMZInactive(mz: MasterZone): boolean {
    return mz.hasOwnProperty('active') && !mz.active;
  }

  isJobDpp(job: JobListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout, masterZones, offices, cMDatesFilter, jobFilters, filterName}
      = getRouteInitData(this.route);
    let isInit = true;
    const masterZone = initFilter.masterZoneId
      ? masterZones.find((item: Office) => item.id === initFilter.masterZoneId)
      : {id: '', name: 'All'};
    const office = initFilter.officeId
      ? offices.find((item: Office) => item.id === initFilter.officeId)
      : {id: '', name: 'All'};
    let predefinedFilters = jobFilters.seq();
    if (this.userContext.masterView) {
      const corporateFilterIds = ['CORPORATE_DUE', 'CORPORATE_PAID'];
      predefinedFilters = predefinedFilters.filter((item: any) => !corporateFilterIds.includes(item.id));
    }
    this.predefinedFilters = predefinedFilters;
    this.datesList = cMDatesFilter;
    this.masterZoneList = masterZones.sort((a: MasterZone, b: MasterZone) => {
      if ((!a.active && !b.active) || (a.active && b.active)) {
        return 0;
      } else if (a.active && !b.active) {
        return  -1;
      }
      return 1;
    });
    this.gridDefinition = this.getGreedDefinition();
    this.gridDefinition.bind(gridLayout);
    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: { ...initFilter, office, masterZone },
      children: {
        masterZoneId: { visible: this.userContext.corporateView },
        masterZone: { visible: this.userContext.corporateView },
        officeId: void 0,
        office: void 0,
        searchString: void 0,
        showFinalized: void 0,
        showDpp: void 0,
        showNotProceeding: void 0,
        isSlabExist: void 0,
        dateType: void 0,
        dateFrom: void 0,
        dateTo: void 0,
        filterType: void 0,
        showActive: void 0,
        showInactiveOffices: { enabled: !initFilter.officeId },
      },
    }));
    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = {
        ...omit(filter,
                'office',
                'officeId',
                'masterZone',
                'masterZoneId',
        ),
      };
      if (!!filter.office?.id) {
        this.filter.officeId = filter.office.id;
      }
      if (!!filter.masterZone?.id) {
        this.filter.masterZoneId = filter.masterZone.id;
      }
      const dateTo = new Date(filter.dateTo || '');
      const dateFrom = new Date(filter.dateFrom || '');
      const fixedFilter = {
        ...this.filter,
        dateFrom: filter.dateFrom ? `${[dateFrom.getFullYear(), dateFrom.getMonth() + 1, dateFrom.getDate()].map(this.stringifyDateComponent).join('-')}` : undefined,
        dateTo: filter.dateTo ? `${[dateTo.getFullYear(), dateTo.getMonth() + 1, dateTo.getDate()].map(this.stringifyDateComponent).join('-')}` : undefined,
      };
      this.dataSource = this.businessService.findAllAsDataSource({...fixedFilter});
      if (!isInit) {
        this.filterService.update(filterName, fixedFilter).subscribe();
      }
      isInit = false;
    });
  }

  ngOnDestroy(): void {
    this.gridService.leaveTable();
  }

  getGreedDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        status: {
          cellClass: ({data}: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
          }[data.statusValue]),
        },
        contractAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        variationsAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        totalAmount: buildNumberColumnLeft(NumberTypes.Price),
        totalAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        estimateCost: buildNumberColumnLeft(NumberTypes.Price),
        estimatedProfit: buildNumberColumnLeft(NumberTypes.Price),
        profitMargin: buildNumberColumnLeft(NumberTypes.Price),
        originalBudget: buildNumberColumnLeft(NumberTypes.Price),
        originalContractAmount: buildNumberColumnLeft(NumberTypes.Price),
        originalProfitMargin: buildNumberColumnLeft(NumberTypes.Price),
        currentBudget: buildNumberColumnLeft(NumberTypes.Price),
        totalInvoices: buildNumberColumnLeft(NumberTypes.Price),
        drawsMadeWithGst: buildNumberColumnLeft(NumberTypes.Price),
        actualProfit: buildNumberColumnLeft(NumberTypes.Price),

        totalOfficeFee: buildNumberColumnLeft(NumberTypes.Price),
        totalOfficeVariationFee: buildNumberColumnLeft(NumberTypes.Price),
        officePaymentDue: buildNumberColumnLeft(NumberTypes.Price),
        totalMasterFee: buildNumberColumnLeft(NumberTypes.Price),
        totalMasterVariationFee: buildNumberColumnLeft(NumberTypes.Price),
        masterPaymentDue: buildNumberColumnLeft(NumberTypes.Price),
        // reportedSaleDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        // reportedSlabDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        saleDateReported: buildDateTimeColumn(DateTimeTypes.ShortDate),
        slabDateReported: buildDateTimeColumn(DateTimeTypes.ShortDate),
        finalizedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        contractDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        contracturalCompletionDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        saleDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        financeApprovedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        csDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        councilApprovedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        authToCommenceDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        contractStartDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        slabDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        lastAccountingDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        finalClaimDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        handoverDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        notProceedingDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowNodeId: (item: JobListItem) => item.id,
        processRowPostCreate: conditionalRowPostCreateDecorator(
          ({ node: { data } }) => isJobElementTotal(data),
          textOverlayRowPostCreate(this.translations.get('lbl_total').toUpperCase())),
      });
  }

  onJobDetailsOpen(job: JobListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobDetails.ANY)) {
      if (job.isDpp) {
        const url = this.angRouter.serializeUrl(
          this.angRouter.createUrlTree([`office-view/${job.officeId}/job-admin/jobs/dpp`, job.id]));

        window.open(url, '_blank');
      } else {
        const url = this.angRouter.serializeUrl(
          this.angRouter.createUrlTree([`office-view/${job.officeId}/job-admin/jobs`, job.id, 'general']));

        window.open(url, '_blank');
      }
    }
  }

  onJobBillingOpen(job: JobListItem): void {
    if (!this.isElement(job)) {
      return;
    }
    this.businessService.onJobBillingOpen(job, 'lbl_jobs');
  }

  goToAel(job: JobListItem): void {
    this.navigationService.goToOfficeAel(job?.officeId || '', {
      label: 'lbl_jobs',
      entityType: 'JOB',
      entityId: job ? job.id : undefined,
   });
  }

  validateDate(): void {
    const dateFrom = this.form.getControl('dateFrom');
    const dateTo = this.form.getControl('dateTo');
    try {
      if (!(new Date(dateFrom.value).getDate())) {
        dateFrom.reset('');
      }
    } catch (e) {
      dateFrom.reset('');
    }
    try {
      if (!(new Date(dateTo.value).getDate())) {
        dateTo.reset('');
      }
    } catch (e) {
      dateTo.reset('');
    }
  }

  onOfficeSelected(office: Office): void {
    if (!office || office.id !== this.form.getControl('officeId').value) {
      this.form.getControl('officeId').setValue(office?.id || '');
    }
    const control =  this.form.getControl('showInactiveOffices');
    control.setValue(false);
    if (office && office.status === OfficeStatus.Deactivated) {
      control.setValue(true);
      control.disable();
    } else if (office && office.id) {
      control.disable();
    } else {
      control.enable();
    }
    this.onSearch();
  }

  loadMasterZones(event: AutocompleteLoadMoreEvent): void {
    event.load(of$(event.query.filter
                     ? this.masterZoneList.filter((masterZone: MasterZone) =>
                                                    masterZone.name.toLowerCase()
                                                      .includes(event.query.filter.toLowerCase()))
                     : [{id: '', name: 'All'}, ...this.masterZoneList]));
  }

  onMasterZoneSelected(masterZone: MasterZone): void {
    if (!masterZone || masterZone.id !== this.form.getControl('masterZoneId').value) {
      this.form.getControl('masterZoneId').setValue(masterZone?.id || '');
      this.resetOffice();
    }
  }

  loadOffice(event: AutocompleteLoadMoreEvent): void {
    let masterZoneId;
    if (this.userContext.corporateView) {
      masterZoneId = this.form.get('masterZoneId').value;
    } else if (this.userContext.masterView) {
      masterZoneId = this.userContext.masterZone?.id;
    }

    if (!masterZoneId) {
      event.load(of$([{id: '', name: 'All'}]));
      return;
    }
    event.load(this.businessService.findAllOfficesByMaster(masterZoneId, event.query.filter));
  }

  onSearch(): void {
    this.validateDate();
    this.form.emitFilter();
  }

  onSelectMasterZone(value: string): void {
    this.resetOffice();
    this.form.emitFilter();
  }

  onJobSpecificationOpen(job: JobListItem): void {
    if (job.isFinalized || job.isNotProceeding && !job.isSpecExist) {
      this.openNewTab([`office-view/${job.officeId}/job-admin/job-specifications`],  { queryParams: {message: 'msg_spec_empty'}});
    } else if (!job.isFinalized && !job.isNotProceeding && !job.isSpecExist) {
      this.openNewTab([`office-view/${job.officeId}/job-admin/job-specifications`],  { queryParams: { createSpec: job.id}});
    } else if (!job.isFinalized && !job.isNotProceeding && job.isSpecExist) {
      this.openNewTab([`office-view/${job.officeId}/job-admin/jobs`, job.id, 'spec']);
    }
  }

  onColorSelectionOpen(job: JobListItem): void {
    if (job.isFinalized || job.isNotProceeding && !job.isCsExist) {
      this.openNewTab([`office-view/${job.officeId}/job-admin/job-color-selection`],  { queryParams: {message: 'msg_cs_empty'}});
    } else if (!job.isFinalized && !job.isNotProceeding && !job.isCsExist) {
      this.openNewTab([`office-view/${job.officeId}/job-admin/job-color-selection`],  { queryParams: {createCS: job.id}});
    } else if (!job.isFinalized && !job.isNotProceeding && job.isCsExist) {
      this.openNewTab([`office-view/${job.officeId}/job-admin/jobs`, job.id, 'color-selection']);
    }
  }

  onJobEstimateOpen(job: JobListItem): void {
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${job.officeId}/job-admin/jobs`, job.id, 'estimate']));

    window.open(url, '_blank');
  }

  onFinancialsOpen(job: JobListItem): void {
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${job.officeId}/job-admin/job-financials`, job.id]));

    window.open(url, '_blank');
  }

  onJobOrderOpen(job: JobListItem): void {
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${job.officeId}/job-admin/job-orders`, job.id]));

    window.open(url, '_blank');
  }

  onJobChangeOrderOpen(job: JobListItem): void {
    const url = this.angRouter.serializeUrl(
      this.angRouter.createUrlTree([`office-view/${job.officeId}/job-admin/job-change-orders`, job.id]));

    window.open(url, '_blank');
  }

  onLoadData(event: any): void {
    const totals = event.totals;
    const total = {};
    for (const key in totals) {
      if (totals.hasOwnProperty(key)) {
        total[key] = totals[key];
      }
    }
    this._total.next([{type: JobElementType.Total, id: 'total-count', variationId: 'variation:footer', ...total  }]);
  }

  isElement(element: any): boolean {
    return element.type !== JobElementType.Total;
  }

  onExport(): void {
    const columns = this.gridDefinition.layout.columns.map((column) =>  ({
      width: column.width,
      column: column.column,
      visible: column.visible,
      position: column.position,
      sortable: column.sortable,
      resizable: column.resizable,
    })).filter((column) => column.visible);
    const process = this.waitingContext.start(WaitingMode.Background);
    this.businessService.export(this.filter, columns).subscribe(
     ({url}) => {
      this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
        title: this.translations.get('lbl_link_to_google_sheet'),
        html: `${this.translations.get('lbl_link_to_generated_doc')}:\n${createLink(url)}`,
        buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
      }).afterClosed();
      process.stop();
       // tslint:disable-next-line:align
    }, (error) => {
       this.notificationService.processAll(error);
       process.stop();
     });
  }

  onRecalculate(): void {
    this.businessService.recalculate().subscribe(() => {
      this.notificationService.success('msg_recalculate_jobs_success');
    },                                           (error) => {
      this.notificationService.processAll(error);
    });
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0'); // tslint:disable-line:no-magic-numbers
  }

  onResetPredefined(): void {
    this.form.getControl('dateType').reset();
    this.form.getControl('dateFrom').reset('');
    this.form.getControl('dateTo').reset('');
    this.form.getControl('filterType').reset('');
    this.onSearch();
  }

  onReset(): void {
    this.form.getControl('dateType').reset();
    this.form.getControl('dateFrom').reset('');
    this.form.getControl('dateTo').reset('');
    this.form.emitFilter();
  }

  onResetAll(): void {
    this.form.getControl('isSlabExist').setValue('');
    this.form.getControl('dateType').reset();
    this.form.getControl('dateFrom').reset('');
    this.form.getControl('dateTo').reset('');
    this.form.getControl('showDpp').setValue(false);
    this.form.getControl('showNotProceeding').setValue(false);
    this.form.getControl('showFinalized').setValue(false);
  }

  onSelectPredefinedFilters(filterName: string): void {
    this.onResetAll();
    switch (filterName)  {
      case 'ALL':
        this.form.getControl('showDpp').setValue(true);
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('showNotProceeding').setValue(true);
        break;
      case 'APPROVED':
        this.form.getControl('dateType').setValue(CMDatesFilter.PermitApproval);
        break;
      case 'NO_SLAB_DOWN':
        this.form.getControl('isSlabExist').setValue(false);
        break;
      case 'SIGNED':
        this.form.getControl('dateType').setValue(CMDatesFilter.ContractSigned);
        break;
      case 'SLAB':
        this.form.getControl('dateType').setValue(CMDatesFilter.Slab);
        break;
      case 'SLAB_NOT_PROCEED':
        this.form.getControl('dateType').setValue(CMDatesFilter.Slab);
        this.form.getControl('showNotProceeding').setValue(false);
        break;
      case 'MASTER_DUE':
        this.form.getControl('showDpp').setValue(true);
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('showFinalized').setValue(true);
        break;
      default:
        this.form.getControl('showDpp').setValue(true);
        this.form.getControl('showActive').setValue(true);
        break;
    }
    this.form.emitFilter();
  }

  goToWorkSheet(): void {
    const filter =  {
      dateType: this.filter.dateType || null,
      filterType: this.filter.filterType || '',
      isSlabExist: this.filter.isSlabExist || '',
      masterZoneId: this.filter.masterZone?.id || this.filter.masterZoneId || '',
      officeId: this.filter.office?.id || this.filter.officeId || '',
      searchString: this.filter.searchString || '',
      showActive: this.filter.showActive || '',
      showDpp: this.filter.showDpp || false,
      showFinalized: this.filter.showFinalized || false,
      showNotProceeding: this.filter.showNotProceeding || false,
    } as WorkSheetFilter;
    if (this.filter.dateFrom) {
      filter.dateFrom = this.filter.dateFrom;
    }

    if (this.filter.dateTo) {
      filter.dateTo = this.filter.dateTo;
    }
    this.router.navigateByUrl(`/contracts-manager/worksheet?filter=${ JSON.stringify(filter)}`);
  }

  validateGDrive(job: JobListItem): void {
    this.businessService.validate(job.id).subscribe(() => {
      this.notificationService.success('msg_validate_job_google_folders_task_added', {jobNumber: job.jobNumber});
    },                                              (error) => {
      this.notificationService.processAll(error);
    });
  }
  private openNewTab(url: string[],  params?: any): void {
    const targetUrl = this.angRouter.serializeUrl(this.angRouter.createUrlTree([...url], params));
    window.open(targetUrl, '_blank');
  }
  private resetOffice(): void {
    this.form.getControl('office').setValue({name: 'All', id: ''});
    this.form.getControl('officeId').setValue('');
  }
}
